import { Component, OnInit } from "@angular/core";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IRecentlySelectedEntity } from "@app/directives/_misc/recently-selected/recently-selected.component";
import { AccountSector } from "@app/enum/account/AccountSector";
import { SortDirection } from "@app/enum/SortDirection";
import { IAggregateFilterEvent, IFilterBarTab, IVerticalFilterListItem } from "@directives/_resourceFilters/filter-bar/filter-bar.models";
import { AccountFeatures } from "@enums/account/AccountFeature";
import { AccountFleetType } from "@enums/account/AccountFleetType";
import { AccountStatus } from "@enums/account/AccountStatus";
import { Platform } from "@enums/account/Platform";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { IAccount } from "@services/account.service";
import { AccountFeatureService } from "@services/accountFeature.service";
import { NavigationService } from "@services/navigation.service";
import { TranslationService } from "@services/translation.service";
import { combineLatest } from "rxjs";
import { IReseller } from "../../_models/IReseller";
import { AccountService } from "../../_services/account.service";
import { FoundationNavigationModule } from "../../foundation.navigation";
import "./customers.component.scss";
import { IResellerAccountMapping, ResellerAccountMappingService } from "@app/services/resellerAccountMapping.service";
import { VPillsComponent } from "@app/directives/_veri/v-pills/v-pills.component";

@UntilDestroy()
@Component({
    selector: "foundation-customers",
    template: require("./customers.component.html")
})
export class FoundationCustomersComponent implements OnInit {

    loading = false;
    customers: IAccount[] = [];
    resellers: IReseller[] = [];

    // ---

    filterTabs: IFilterBarTab<IAccount>[] = [];

    Platform = Platform;
    AccountFleetType = AccountFleetType;
    AccountSector = AccountSector;
    AccountStatus = AccountStatus;

    featureItems: IVerticalFilterListItem<IAccount>[] = [];
    resellerItems: IVerticalFilterListItem<IAccount>[] = [];
    filterEvent: IAggregateFilterEvent<IAccount>;

    // Tables
    tableColumns: IVTableColumn<IAccount>[];
    tableRows: IVTableRow[];

    constructor(
        private readonly navigationService: NavigationService,
        private readonly accountService: AccountService,
        private readonly accountFeatureService: AccountFeatureService,
        private readonly translationService: TranslationService,
        private readonly vTableService: VTableService,
        private readonly resellerAccountMappingService: ResellerAccountMappingService) {

        this.createTabs();
        this.createAccountFeatureFilterItems();
    }

    ngOnInit(): void {
        this.createTable();
        this.getData();
    }

    onRowClicked(row: IVTableRow): void {
        this.viewCustomer(row.data);
    }

    onRecentItemClicked(recentItem: IRecentlySelectedEntity) {
        const customer = this.customers.find(c => c.Id == recentItem.id);
        this.viewCustomer(customer);
    }

    viewCustomer(customer: IAccount): void {
        this.navigationService.go(FoundationNavigationModule.CustomerView, { customerId: customer.Id });
    }

    addCustomer(): void {
        this.navigationService.go(FoundationNavigationModule.CustomerNew);
    }

    onFilterChanged(event: IAggregateFilterEvent<IAccount>): void {

        // We sometimes get weird events come through, need to understand why, until then this fixes a bug
        if (!event.trigger) {
            return;
        }

        this.filterEvent = event;
    }

    // ---

    private getData(invalidateCache = false): void {
        this.loading = true;

        combineLatest([
             this.accountService.getCustomerAccounts(invalidateCache),
             this.accountService.getResellers(),
             this.resellerAccountMappingService.getMappings()
        ]).pipe(
            untilDestroyed(this))
            .subscribe({
                next: ([customers, resellers, resellerMappings]) => {
                    this.customers = customers;
                    this.resellers = resellers;

                    this.mapResellersAndFeaturesToCustomers(resellerMappings);
                    
                    this.tableRows = this.vTableService.createTableRows(this.customers, this.tableColumns);
                    this.getResellersFilterItems();
                    this.loading = false;
                }
            })
    }

    private mapResellersAndFeaturesToCustomers(resellerMappings: IResellerAccountMapping[]): void {
        // TODO:
        // Use case for a table cell that performs a callback to get its data, e.g. renders only as it goes
        // this would be useful for reseller names etc
        // would be useful to show number of assets (so only calculate on demand)

        this.customers.forEach(account => {

            account.ResellerIds = resellerMappings.filter(mapping => mapping.AccountId === account.Id && !mapping.EndTime).map(mapping => mapping.ResellerId);

            this.createResellerPills(account);
            account.featureString = this.accountFeatureService.getFeatureString(account.Features);
        });
    }

    private createResellerPills(account: IAccount): void {
        account.resellerPills = [];

        const resellerPillsExportData: string[] = [];
        let extraCount = 0;

        account.ResellerIds.forEach((resellerId, index) => {
            const reseller = this.resellers.find(r => r.Id === resellerId);

            resellerPillsExportData.push(reseller.Description);
            if (index < 1) {
                account.resellerPills.push({
                    label: reseller?.Description,
                    hovering: false,
                    data: reseller,
                    tooltip: reseller?.Description
                });
            } else {
                extraCount++;
            }
        });
                
        if (extraCount > 0) {
            account.resellerPills.push({
                label: `+${extraCount}`,
                hovering: true,
                tooltip: resellerPillsExportData.slice(1).join(", ")
            });
        }
    }

    private createTable(): void {

        this.tableColumns = [
            {
                header: this.vTableService.createDateTableHeader({ tKey: "Common.Created", sorted: true, sortDirection: SortDirection.Descending }),
                createCellFunction: (customer) => this.vTableService.createDateCell(new Date(customer.DateTimeSetup), { dateTime: false })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Name", minWidth: 100 }),
                createCellFunction: (customer) => this.vTableService.createTableCell(customer.CompanyName)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "FoundationCustomers.ParentCompany", minTableWidth: 650 }),
                createCellFunction: (customer) => this.vTableService.createTableCell(customer, {
                    component: VPillsComponent,
                    componentInputs: {
                        pills: customer.resellerPills
                    }
                })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "FoundationCustomers.Platform", minTableWidth: 1000, searchable: false, width: 100 }),
                createCellFunction: (customer) => this.vTableService.createTableCell(Platform[customer.Platform])
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "FoundationCustomers.FleetType", minTableWidth: 1200, width: 100 }),
                createCellFunction: (customer) => this.vTableService.createTableCell(AccountFleetType[customer.FleetType])
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "FoundationCustomers.Sector", minTableWidth: 1400, width: 140 }),
                createCellFunction: (customer) => this.vTableService.createTableCell(AccountSector[customer.SectorId])
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Status", minTableWidth: 1300, width: 80 }),
                createCellFunction: (customer) => this.vTableService.createTableCell(AccountStatus[customer.Status])
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "AccountFeaturesEnum.Title", width: 250 }),
                createCellFunction: (customer) => this.vTableService.createTableCell(customer.featureString)
            }
        ];
    }

    private createTabs(): void {
        this.filterTabs = [];

        this.createTabDefinition(this.translationService.tEnum(AccountStatus, "AccountStatus", AccountStatus.Active), a => a.Status == AccountStatus.Active);

        this.createTabDefinition("Verilocation", a => a.Platform == Platform.Verilocation && a.Status == AccountStatus.Active);
        this.createTabDefinition("RTL", a => a.Platform == Platform.RTL && a.Status == AccountStatus.Active);
        this.createTabDefinition("Isotrak", a => a.Platform == Platform.Isotrak && a.Status == AccountStatus.Active);
        this.createTabDefinition("Alcolock", a => a.Platform == Platform.Alcolock && a.Status == AccountStatus.Active);

        this.createTabDefinition(this.translationService.tEnum(AccountStatus, "AccountStatus", AccountStatus.Disabled), a => a.Status == AccountStatus.Barred || a.Status == AccountStatus.Disabled);
    }

    private createTabDefinition(title: string, callback: (account: IAccount) => boolean) {

        this.filterTabs.push({ title: title, predicate: callback });
    }

    private createAccountFeatureFilterItems(): void {
        this.featureItems = [];
        var allAccountFeatures = Object.keys(AccountFeatures).filter(key => !isNaN(Number(AccountFeatures[key])) && AccountFeatures[key] > 0);

        allAccountFeatures.forEach(enumKey => {
            var featureDesc = this.accountFeatureService.createFeatureDescription(enumKey);

            if (featureDesc) {
                this.featureItems.push({
                    name: featureDesc.name,
                    predicate: (account) => {
                        return this.accountFeatureService.accountHasFeatureAccess(account, featureDesc.feature);
                    }
                });
            }
        });
    }

    private getResellersFilterItems(): void {
        this.resellerItems = [];

        this.resellers.forEach(reseller => {
            const isPresentForAnyAccount = this.customers.some(x => x.ResellerIds.some(rId => rId === reseller.Id));

            if (!isPresentForAnyAccount) {
                return;
            }

            const filter: IVerticalFilterListItem<IAccount> = {
                name: reseller.Description,
                predicate: (account) => account.ResellerIds.some(rId => rId === reseller.Id),
            }

            filter.count = this.customers.filter(d => filter.predicate(d)).length;
            this.resellerItems.push(filter);
        });
    }
}
